import { getMomentDate } from '../../../Utils/dates.util';
import { buildScheduleItem } from '../../../Components/Calendar/calendar.helper';
import apiClient from '../../../Services/ApiClient';
import { getFormattedPriceAsFloat } from '../../Base/Services/priceFormat.service';
import useNotification from '../../../Hooks/useNotification.hook';
import { ServiceScheduleShiftFormValues } from '../Components/ServiceScheduleShiftForm/ServiceScheduleShiftForm';
import moment, { Moment } from 'moment';
import { ServiceScheduleType } from '../../../graphql/graphql';
import { useState } from 'react';
import { useAppContext } from '../../../Services/AppContext';
import useTranslation from '../../../Hooks/useTranslation';
import useApiClient from '../../Base/Hooks/useApiClient';

export default function useServiceSchedulesHelper() {
	const { showNotificationSuccess } = useNotification();
	const { currency, vatRate } = useAppContext();
	const { t } = useTranslation();
	const { displayError } = useApiClient();
	const [workingHours, setWorkingHours] = useState<
		{ from: Moment; to: Moment } | undefined
	>(undefined);
	const [workingHoursExtra, setWorkingHoursExtra] = useState<
		{ from: Moment; to: Moment } | undefined
	>(undefined);

	const calculateTimesFromValues = (values: ServiceScheduleShiftFormValues) => {
		const { date, individualTime, workingTime } = values;
		let { timeTo, timeFrom } = values;

		const dateTo = date.clone().set({
			hour: 23,
			minute: 59,
			second: 59,
		});
		const dateFrom = date.set({
			hour: 0,
			minute: 0,
			second: 0,
		});

		if (individualTime) {
			const timeFromMoment = moment(timeFrom);
			const timeToMoment = moment(timeTo);
			timeFrom = date.clone().set({
				hour: timeFromMoment.hours(),
				minute: timeFromMoment.minutes(),
				second: timeFromMoment.seconds(),
			});
			timeTo = date.clone().set({
				hour: timeToMoment.hours(),
				minute: timeToMoment.minutes(),
				second: timeToMoment.seconds(),
			});
		} else {
			if (workingTime === ServiceScheduleType.Shift && workingHours) {
				timeFrom = workingHours.from;
				timeTo = workingHours.to;
			} else if (
				workingTime === ServiceScheduleType.ShiftExtra &&
				workingHoursExtra
			) {
				timeFrom = workingHoursExtra.from;
				timeTo = workingHoursExtra.to;
			}
		}

		return { dateTo, dateFrom, timeTo, timeFrom };
	};

	const createServiceSchedule = async ({
		workplaceId,
		values,
	}: {
		workplaceId: string;
		values: ServiceScheduleShiftFormValues;
	}) => {
		values.date = getMomentDate(values.date);

		const { dateFrom, dateTo, timeFrom, timeTo } =
			calculateTimesFromValues(values);

		try {
			if (values.workingTime) {
				const serviceSchedule = await apiClient.post(
					'/workplaces/' + workplaceId + '/services-schedule/',
					{
						dateFrom: dateFrom.format(),
						dateTo: dateTo.format(),
						form: 'standard',
						type: values.workingTime.toLowerCase(),
					},
				);

				if (serviceSchedule && timeTo && timeFrom) {
					const scheduleId = serviceSchedule.data.id;
					const preparedObject = buildScheduleItem({
						...values,
						workplaceMembers: [values.workplaceMember],
						timeTo,
						timeFrom,
						price:
							values.price && values.extraPayment
								? {
										priceWithVat: getFormattedPriceAsFloat(values.price),
										currency,
										vatRate,
								  }
								: undefined,
					});
					await apiClient.post(
						'/workplaces/' +
							workplaceId +
							'/services-schedule/' +
							scheduleId +
							'/items/',
						preparedObject,
					);
					showNotificationSuccess({
						content: t('component.workplaceSettings.scheduleEventCreated'),
					});

					return true;
				}
			}
		} catch (e) {
			displayError(e);
		}
	};

	const updateServiceSchedule = async ({
		workplaceId,
		values,
		updateFuture,
		serviceScheduleId,
		serviceScheduleItemId,
	}: {
		workplaceId: string;
		values: ServiceScheduleShiftFormValues;
		updateFuture: boolean;
		serviceScheduleId: string;
		serviceScheduleItemId: string;
	}) => {
		values.date = getMomentDate(values.date);

		const { dateFrom, dateTo, timeFrom, timeTo } =
			calculateTimesFromValues(values);

		const days: Record<string, string> = {
			Monday: 'mon',
			Tuesday: 'tue',
			Wednesday: 'wed',
			Thursday: 'thu',
			Friday: 'fri',
			Saturday: 'sat',
			Sunday: 'sun',
		};

		const membersToSave: string[] = [values.workplaceMember.value as string];

		const updateData: {
			day: string;
			timeFrom: string;
			timeTo: string;
			workplaceMemberIds: string[];
			price: {
				priceWithVat: number;
				currency: string;
				vatRate: number;
			} | null;
			interval?: number;
			dateFrom: string;
			dateTo: string;
			updateFuture: boolean;
			repeatInterval?: string;
			repeatTo?: string;
			canOrderImmediate: boolean;
		} = {
			day: days[values.date.format('dddd')],
			timeFrom: moment(timeFrom).format('HH:mmZ'),
			timeTo: moment(timeTo).format('HH:mmZ'),
			workplaceMemberIds: membersToSave,
			price:
				values.price && values.extraPayment
					? {
							priceWithVat: getFormattedPriceAsFloat(values.price),
							currency,
							vatRate,
					  }
					: null,
			interval: values.guaranteedTime,
			dateFrom: dateFrom.format(),
			dateTo: dateTo.format(),
			canOrderImmediate: true,
			updateFuture,
		};

		if (
			!updateFuture &&
			values.repeatInterval &&
			values.repeatInterval.value !== 'never' &&
			values.repeatTo
		) {
			updateData.repeatInterval = values.repeatInterval.value as string;
			updateData.repeatTo = getMomentDate(values.repeatTo).format('YYYY-MM-DD');
		}

		try {
			await apiClient.patch(
				`/workplaces/${workplaceId}/services-schedule/${serviceScheduleId}/items/${serviceScheduleItemId}`,
				updateData,
			);
			return true;
		} catch (e) {
			displayError(e);
		}
	};

	return { createServiceSchedule, updateServiceSchedule };
}
